/**
 * <dispatch-line>: de live verzendregel ("Bestel binnen 1u 42m, dan verzenden we
 * vandaag" / "Nu besteld, morgen verzonden"). Staat onder de koopknop en in de
 * winkelwagen; beide gebruiken snippets/dispatch-line.liquid.
 *
 * Alles komt uit data-attributen die Liquid uit de theme-instellingen vult: de
 * cut-off, de verzenddagen, de tijdzone en de sluitingsdagen. Niets over tijd staat
 * hier vast — zo liepen 22:59 in het script en 13:00 op de verzendpagina vroeger
 * uit elkaar.
 *
 * De server zet al een kloppende algemene zin neer ("Vóór 13:00 besteld, vandaag
 * verzonden"), dus zonder JS staat er geen lege regel en is er geen sprong als dit
 * script hem vervangt.
 *
 * Eén keer per minuut bijwerken, op de minuutgrens, zonder seconden: het is een
 * echte deadline, maar hij hoort rustig te tellen, niet te tikken. Om 13:00 schuift
 * hij zonder herladen door naar de volgende toestand.
 */
class DispatchLine extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #lastHtml = '';
  #rendered = '';

  /**
   * In de cart drawer morpht de theme na elke wijziging de nieuwe HTML over de oude
   * heen: het element blijft, maar de serverzin komt terug ("vandaag verzonden", ook
   * op vrijdagmiddag). Dan meteen opnieuw rekenen.
   */
  #observer = new MutationObserver(() => {
    clearTimeout(this.#timer);
    this.#tick();
  });

  connectedCallback() {
    this.#tick();
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer.disconnect();
  }

  #tick = () => {
    const html = this.#message();
    const target = this.querySelector('[data-dispatch-text]');
    // Geen kloppende boodschap (ontbrekende vertaling, ongeldige tijdzone): de
    // serverzin blijft staan in plaats van een gok.
    // Vergeleken met wat de browser er na de vorige keer van maakte, niet met de string
    // zelf: genormaliseerde HTML wijkt daar soms van af, en dan zou de observer
    // hierboven eindeloos blijven zetten.
    if (html && target && !(html === this.#lastHtml && target.innerHTML === this.#rendered)) {
      target.innerHTML = html;
      this.#lastHtml = html;
      this.#rendered = target.innerHTML;
    }

    const now = new Date();
    const msToNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds() + 250;
    this.#timer = setTimeout(this.#tick, msToNextMinute);
  };

  /** @returns {string} */
  #message() {
    const data = this.dataset;
    const cutoff = (data.cutoff || '').trim();
    const messages = [data.msgCountdown, data.msgTomorrow, data.msgDay, data.fmtHm, data.fmtM];
    if (!cutoff || messages.some((m) => !m || /Translation missing/.test(m))) return '';

    const [h, m] = cutoff.split(':');
    const cutoffMinutes = parseInt(h, 10) * 60 + parseInt(m || '0', 10);
    if (Number.isNaN(cutoffMinutes)) return '';

    const shippingDays = (data.shippingDays || '1,2,3,4,5').split(/[\s,]+/).filter(Boolean).map(Number);
    const holidays = (data.holidays || '').split(/[\s,]+/).filter(Boolean);

    // "Nu" in de tijdzone van het magazijn, niet op de klok van de bezoeker.
    /** @type {Record<string, string>} */
    const parts = {};
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: data.timezone || 'Europe/Amsterdam',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
        .formatToParts(new Date())
        .forEach((p) => (parts[p.type] = p.value));
    } catch (error) {
      return '';
    }

    const nowMinutes = (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10);

    // 12:00 UTC op de kalenderdatum van het magazijn: dagen optellen kan dan nooit
    // over een zomertijdwissel struikelen.
    const cursor = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day, 12));
    const isoDate = (/** @type {Date} */ d) => d.toISOString().slice(0, 10);
    const isoWeekday = (/** @type {Date} */ d) => (d.getUTCDay() === 0 ? 7 : d.getUTCDay());
    const dispatches = (/** @type {Date} */ d) =>
      shippingDays.includes(isoWeekday(d)) && !holidays.includes(isoDate(d));

    if (dispatches(cursor) && nowMinutes < cutoffMinutes) {
      const left = cutoffMinutes - nowMinutes;
      const hours = Math.floor(left / 60);
      const minutes = left % 60;
      const time =
        hours > 0
          ? data.fmtHm.replace('%%H%%', String(hours)).replace('%%M%%', String(minutes))
          : data.fmtM.replace('%%M%%', String(minutes));
      return data.msgCountdown.replace('%%TIME%%', time);
    }

    for (let i = 1; i <= 21; i++) {
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      if (!dispatches(cursor)) continue;

      // "Morgen" alleen als het echt morgen is: vrijdagmiddag is dat maandag.
      if (i === 1) return data.msgTomorrow;

      let dayName;
      try {
        dayName = new Intl.DateTimeFormat(document.documentElement.lang || 'nl', {
          weekday: 'long',
          timeZone: 'UTC',
        }).format(cursor);
      } catch (error) {
        dayName = new Intl.DateTimeFormat('nl', { weekday: 'long', timeZone: 'UTC' }).format(cursor);
      }
      return data.msgDay.replace('%%DAY%%', dayName);
    }

    // Drie weken dicht en nog geen verzenddag: laat de serverzin staan.
    return '';
  }
}

if (!customElements.get('dispatch-line')) {
  customElements.define('dispatch-line', DispatchLine);
}
